<script setup lang="ts">
import { Button } from "@frontiers-vue3/public-pages";
import RTOverviewSubmissionBox from "../RTOverviewSubmissionBox/RTOverviewSubmissionBox.vue";
import RTOverviewArticleTypesList from "../RTOverviewArticleTypesList/RTOverviewArticleTypesList.vue";
import RTOverviewBrakgroundFeeProgram from "./RTOverviewBrakgroundFeeProgram/RTOverviewBrakgroundFeeProgram.vue";
import type { IRTOverviewBackground } from "./IRTOverviewBackground";
import type { ISanitizeOptions } from "@frontiers-vue3/public-pages";
import { Flip } from "gsap/Flip";
import { scrollToItem } from "~/helpers";

const props = defineProps<IRTOverviewBackground>();

const sanitize = inject("sanitize") as (
  html: string,
  options?: ISanitizeOptions
) => string;

const { getIsSubmitManuscriptEnabled } = useMain();
const { showInstitutionsBlock, isResearchTopicImportantNoteVisible } =
  useEnvironmentConfigStore().getConfig();
const terminology = useTerminologyStore();
const desc = ref(
  sanitize(props.researchTopic.description, {
    ALLOWED_TAGS: ["b", "strong", "a", "br", "img", "i", "em", "sub", "sup"]
  })
);

const hasLimitDescription = ref<boolean>(true);
const expandableRef = ref<HTMLDivElement | null>(null);
const expandableSecondaryRef = ref<HTMLDivElement | null>(null);

const handleShowMoreButton = (): void => {
  hasLimitDescription.value = !hasLimitDescription.value;
  const state = Flip.getState(expandableRef.value);
  let stateSecondary;
  if (expandableSecondaryRef.value) {
    stateSecondary = Flip.getState(expandableSecondaryRef.value);
  }
  if (hasLimitDescription.value) {
    expandableRef.value?.classList.remove(
      "RTOverviewBackground__expandable--noLimit"
    );
  } else {
    expandableRef.value?.classList.add(
      "RTOverviewBackground__expandable--noLimit"
    );
  }
  const duration = hasLimitDescription.value ? 0.2 : 0.3;
  const ease = "power2.in";
  Flip.from(state, {
    duration,
    absolute: false,
    ease
  });
  if (stateSecondary && hasLimitDescription.value) {
    Flip.from(stateSecondary, {
      duration,
      absolute: true,
      ease
    });
    scrollToItem("MainTabs");
  }
};

const rtKeywords = computed((): string => {
  if (!props.researchTopic.keywords) {
    return "";
  }
  return props.researchTopic.keywords.map((rtKey) => rtKey.title).join(", ");
});

const abstractSubmissionDeadline = computed((): Date => {
  return new Date(props.researchTopic.abstractSubmissionDeadline);
});

const manuscriptSubmissionDeadline = computed((): Date => {
  return new Date(props.researchTopic.manuscriptSubmissionDeadline);
});

const manuscriptSubmissionExtendedDeadline = computed((): Date => {
  return new Date(props.researchTopic.manuscriptSubmissionExtendedDeadline);
});

const displayFeeProgram = computed(() => {
  return showInstitutionsBlock && props.researchTopic.isOpenForSubmission;
});

const textButton = computed(() => {
  return hasLimitDescription.value ? "Show more" : "Show less";
});

const textIcon = computed(() => {
  return hasLimitDescription.value ? "ChevronDown" : "ChevronUp";
});
</script>
<template>
  <section>
    <h2 class="Section__title">
      About this {{ terminology.getTerm("researchTopic") }}
    </h2>

    <!-- Submission box -->
    <RTOverviewSubmissionBox
      :abstract-submission-deadline="abstractSubmissionDeadline"
      :manuscript-submission-deadline="manuscriptSubmissionDeadline"
      :manuscript-submission-extended-deadline="
        manuscriptSubmissionExtendedDeadline
      "
      :created-date="new Date(researchTopic.createdDate)" />

    <div class="RTOverviewBackground">
      <h3 class="RTOverviewBackground__title">Background</h3>

      <div ref="expandableRef" class="RTOverviewBackground__expandable">
        <p v-html="desc"></p>

        <div
          ref="expandableSecondaryRef"
          class="RTOverviewBackground__expandable__secondary">
          <div
            v-if="researchTopic.imageUrl"
            class="RTOverviewBackground__moreTextImg">
            <img
              :src="researchTopic.imageUrl"
              :alt="`${terminology.getTerm(
                'researchTopic'
              )} Research topic image`" />
          </div>

          <!-- Article types -->
          <RTOverviewArticleTypesList
            v-if="getIsSubmitManuscriptEnabled"
            :research-topic="researchTopic" />

          <!-- Fee program -->
          <RTOverviewBrakgroundFeeProgram v-if="displayFeeProgram" />

          <!-- Keywords & Important Note -->
          <div class="RTOverviewBackground__keywords">
            <p v-if="rtKeywords"><strong>Keywords: </strong>{{ rtKeywords }}</p>
            <p v-if="isResearchTopicImportantNoteVisible">
              <strong>Important note: </strong>
              All contributions to this
              {{ terminology.getTerm("researchTopic") }} must be within the
              scope of the section and journal to which they are submitted, as
              defined in their mission statements.
              {{ terminology.getTerm("tenantName") }} reserves the right to
              guide an out-of-scope manuscript to a more suitable section or
              journal at any stage of peer review.
            </p>
          </div>
        </div>
      </div>
    </div>
    <Button
      :text="textButton"
      type="linkButton"
      size="Medium"
      :icon="textIcon"
      @click="handleShowMoreButton" />
  </section>
</template>
<style lang="scss">
.RTOverviewBackground {
  margin: 32px 0;
  overflow: hidden;
  transition: max-height 0.3s ease-out;

  &__expandable {
    @include clamp(3);

    &--noLimit {
      -webkit-line-clamp: unset;
      line-clamp: unset;

      .RTOverviewBackground__expandable__secondary {
        display: initial;
      }
    }

    sub,
    sup {
      all: revert;
      font-size: 75%;
    }

    i,
    em {
      font-style: italic;
    }

    &__secondary {
      display: none;
    }
  }

  .RTOverviewBackground__title {
    @include f-20-500;
    margin: 0 0 24px 0;
  }

  p {
    -webkit-line-clamp: unset;
    line-clamp: unset;
    margin-bottom: 0;
  }

  a {
    color: var(--maincolor50);
    font-weight: 700;

    &:hover {
      color: var(--maincolor70);
    }
  }

  b {
    font-weight: 500;
  }

  strong {
    font-weight: 700;
  }

  &__keywords {
    border-top: 1px solid $grey40;
    margin-top: 32px;

    p {
      margin-top: 32px;
      margin-bottom: 16px;
      @include f-12-300;
      strong {
        font-weight: 700;
      }
    }
  }

  img {
    height: auto;
  }

  &__moreTextImg {
    width: var(--main-text-img-size);
    height: var(--main-text-img-size);
    margin-top: 24px;
    position: relative;
    // opacity: 0;

    &--noLimit {
      opacity: 1;
    }

    @media #{$break-xs} {
      width: 100%;
      height: auto;
    }

    img {
      margin: 0;
      width: 100%;
      height: 100%;
      -o-object-fit: cover;
      object-fit: cover;
    }
  }

  .FeeProgram {
    margin: 56px 0;
  }
}
</style>
